import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import { createLogger } from './utils/debugLogger';

// Theme Provider
import ThemeProvider from './contexts/ThemeContext';

// Market Condition Provider
import { MarketConditionProvider } from './contexts/MarketConditionContext';

// Notes Modal Provider
import { NotesModalProvider, CLEAR_NOTES_MODAL_EVENT } from './contexts/NotesModalContext';

// WebSocket initialization
import { initSocket, disconnectSocket } from './services/socketService';

// Session management
import { useSessionTimeout, getSessionTimeoutFromStorage } from './utils/sessionManager';
import { clearAllChartDataCaches } from './utils/chartDataCache';
import { clearAllBaseOverrides } from './utils/baseOverrides';
import { clearAllThemeOverrides } from './utils/themeOverrides';

// Components
import Navigation from './components/Navigation';
import Breadcrumbs from './components/Breadcrumbs';
import NavigationPanel from './components/NavigationPanel';
import BalanceUpdateAlert from './components/BalanceUpdateAlert';
import StopLossNotification from './components/StopLossNotification';
import GlobalNotesModal from './components/GlobalNotesModal';
import NotesModalManager from './components/NotesModalManager';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import PortfolioDetails from './pages/PortfolioDetails';
import AccountDetails from './pages/AccountDetails';
import AggregatedTickers from './pages/AggregatedTickers';
import TickerSearch from './pages/TickerSearch';
import AllAccounts from './pages/AllAccounts';
import Transactions from './pages/Transactions';
import Settings from './pages/Settings';
import AdminSettings from './pages/AdminSettings';
import Profile from './pages/Profile';
import ActivityLog from './pages/ActivityLog';
import Trades from './pages/Trades';
import TradeDetails from './pages/TradeDetails';
import Orders from './pages/Orders';
import Plan from './pages/Plan';
import Watchlist from './pages/Watchlist';
import Ants from './pages/Ants';
import NewIPOs from './pages/NewIPOs';
import ETFs from './pages/ETFs';
import Stopouts from './pages/Stopouts';
import ATHigh from './pages/ATHigh';
import Turnarounds from './pages/Turnarounds';
import Cyclicals from './pages/Cyclicals';
import GLeaders from './pages/GLeaders';
import GLeaderDetails from './pages/GLeaderDetails';
import Inverses from './pages/Inverses';
import Steroids from './pages/Steroids';
import HVol from './pages/HVol';
import Minervini from './pages/Minervini';
import Leaders from './pages/Leaders';
import EPS from './pages/EPS';
import VCP from './pages/VCP';
import Basing from './pages/Basing';
import Readylist from './pages/Readylist';
import Notes from './pages/Notes';
import NotesDetails from './pages/NotesDetails';
import Wisdom from './pages/Wisdom';
import Strategies from './pages/Strategies';
import Screen from './pages/Screen';
import Themes from './pages/Themes';
import ThemeDetails from './pages/ThemeDetails';
import Livermore from './pages/Livermore';
import Play from './pages/Play';

const logger = createLogger(
  'appShell',
  'Authentication/session lifecycle',
  false,
  'DEBUG'
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [userId, setUserId] = useState(localStorage.getItem('userId') || null);
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem('isAdmin') === 'true');
  const [loading, setLoading] = useState(true);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    const savedState = localStorage.getItem('navPanelCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [navPinned, setNavPinned] = useState(() => {
    const savedState = localStorage.getItem('navPanelPinned');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAutoLogoutAlert, setShowAutoLogoutAlert] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const triggerThemeCheck = useCallback(() => {
    window.dispatchEvent(new Event('theme:force-update'));
  }, []);
  
  // Initialize mobile nav state to be visible by default on larger screens
  const [mobileNavVisible, setMobileNavVisible] = useState(() => {
    // In mobile view, start with nav hidden; on desktop, follow the collapsed state
    return window.innerWidth > 768;
  });

  // Define handleLogout with useCallback to prevent recreating it on every render
  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('keepLoggedInUntil');
    // The All Accounts chart's localStorage cache (chartDataCache.js) isn't
    // scoped by user_id -- without clearing it here, the next user to log in
    // on this browser would briefly see the previous user's cached balance
    // chart data rendered before the fresh fetch overwrites it.
    clearAllChartDataCaches();
    // Same class of bug, same fix: TickerTable's base/theme overrides
    // (baseOverrides.js/themeOverrides.js) are also unscoped localStorage
    // caches that unconditionally overlay the server's value on every
    // ticker fetch, and had no caller of their clear function anywhere in
    // the app -- an override set once persisted forever in this browser,
    // permanently masking the real value even after it legitimately
    // changed server-side (on this account or a different user's, on a
    // shared browser).
    clearAllBaseOverrides();
    clearAllThemeOverrides();
    // Same class of bug again: NotesModalProvider persists isVisible/ticker/
    // notes (real note content) to localStorage and is mounted above
    // <Router> so it never remounts on logout -- without this, the next
    // user on this browser could inherit another user's open notes modal.
    window.dispatchEvent(new CustomEvent(CLEAR_NOTES_MODAL_EVENT));

    setIsAuthenticated(false);
    setUserId(null);
    setIsAdmin(false);
  }, []); // No dependencies needed as it only uses setState functions which are stable

  // Auto logout due to inactivity - also memoized properly
  const handleSessionTimeout = useCallback(() => {
    if (isAuthenticated) {
      // Check if "keep me logged in for today" is active before logging out
      const keepLoggedInUntil = localStorage.getItem('keepLoggedInUntil');
      if (keepLoggedInUntil) {
        const expirationTime = parseInt(keepLoggedInUntil, 10);
        // If keepLoggedInUntil is still valid, don't log out
        if (Date.now() < expirationTime) {
          logger.debug('Keep me logged in is active, skipping auto-logout');
          return;
        }
      }

      logger.warn('Session timeout detected, logging out');
      handleLogout();
    }
  }, [isAuthenticated, handleLogout]);

  // Use a ref to control when the session timeout should be active
  const sessionTimeoutRef = useRef(null);
  
  // Call useSessionTimeout at the top level with a ref to prevent dependency cycles
  const sessionTimeout = isAuthenticated ? getSessionTimeoutFromStorage() : 0;
  useSessionTimeout(
    sessionTimeoutRef.current ? handleSessionTimeout : () => {},
    sessionTimeout
  );
  
  // Update the ref when dependencies change, not the hook itself
  useEffect(() => {
    sessionTimeoutRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Modify the useEffect that checks authentication
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    const storedUserId = localStorage.getItem('userId');
    const keepLoggedInUntil = localStorage.getItem('keepLoggedInUntil');
    
    // Check password expiration
    const passwordExpiredFlag = localStorage.getItem('passwordExpired');

    if (passwordExpiredFlag === 'true') {
      setPasswordExpired(true);
    } else {
      setPasswordExpired(false);
    }

    // Check if there's a "keep logged in until" timestamp and if it's still valid
    if (keepLoggedInUntil) {
      const expirationTime = parseInt(keepLoggedInUntil, 10);
      const now = Date.now();
      
      // Debug logs to help identify any issues
      logger.debug('Session keep-alive check', {
        currentTime: new Date(now).toLocaleString(),
        expirationTime: new Date(expirationTime).toLocaleString(),
        isSessionValid: now < expirationTime
      });

      // If the timestamp has expired, remove it and the user should be logged out
      if (now >= expirationTime) {
        logger.warn('Session expired, removing keepLoggedInUntil');
        localStorage.removeItem('keepLoggedInUntil');
        // Don't set authenticated state - user should be logged out
      } else {
        // If keepLoggedInUntil is still valid, ensure the session timeout control knows about it
        logger.debug('Keep me logged in is still valid');
      }
    }
    
    if (token && storedUserId) {
      setIsAuthenticated(true);
      setUserId(storedUserId);
      triggerThemeCheck();

      // Note: WebSocket initialization will be handled by the useEffect that watches isAuthenticated
    }
    
    setLoading(false);
  }, [triggerThemeCheck]);

  // Update the existing WebSocket initialization useEffect
  useEffect(() => {
    // Only initialize socket if user becomes authenticated
    if (isAuthenticated) {
      logger.debug('Authentication state changed to authenticated, ensuring WebSocket connection');
      initSocket();
    } else if (!isAuthenticated) {
      // Disconnect socket when user logs out
      logger.info('User is not authenticated, disconnecting WebSocket');
      disconnectSocket();
    }

    return () => {
      // Clean up socket connection when component unmounts
      if (isAuthenticated) {
        logger.debug('App component unmounting, cleaning up socket');
        disconnectSocket();
      }
    };
  }, [isAuthenticated]);

  // This useEffect was also causing some issues - simplify and memoize
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'navPanelCollapsed') {
        const collapsed = localStorage.getItem('navPanelCollapsed');
        if (collapsed !== null) {
          setNavCollapsed(JSON.parse(collapsed));
        }
      }
      
      if (event.key === 'navPanelPinned') {
        const pinned = localStorage.getItem('navPanelPinned');
        if (pinned !== null) {
          setNavPinned(JSON.parse(pinned));
        }
      }
    };

    const handleNavCollapseChange = (event) => {
      setNavCollapsed(event.detail.collapsed);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('navCollapseChange', handleNavCollapseChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('navCollapseChange', handleNavCollapseChange);
    }
  }, []);

  // Effect to detect screen size changes for mobile nav
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Auto-show navigation on larger screens
      if (!mobile) {
        setMobileNavVisible(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogin = useCallback((userData) => {
    if (userData.token) {
      localStorage.setItem('token', userData.token);
      localStorage.setItem('userId', userData.user_id);
      localStorage.setItem('username', userData.username || 'User');
      localStorage.setItem('isAdmin', userData.is_admin ? 'true' : 'false');
      
      setIsAuthenticated(true);
      setUserId(userData.user_id);
      setIsAdmin(userData.is_admin || false);
      triggerThemeCheck();
      
      logger.info('User authenticated', { username: userData.username });
      logger.debug('Admin status', { role: userData.is_admin ? 'Admin' : 'Regular user' });
    }
  }, [triggerThemeCheck]);

  const toggleMobileNav = useCallback(() => {
    const newVisibleState = !mobileNavVisible;
    setMobileNavVisible(newVisibleState);
    
    // Dispatch event for other components without causing a circular update
    const mobileNavEvent = new CustomEvent('mobileNavChange', { 
      detail: { visible: newVisibleState } 
    });
    window.dispatchEvent(mobileNavEvent);
  }, [mobileNavVisible]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const mainLeftOffset = isAuthenticated && !isMobile
    ? (navPinned ? '280px' : (navCollapsed ? '60px' : '280px'))
    : '0';

  return (
    <ThemeProvider>
      <MarketConditionProvider>
        <NotesModalProvider>
          <Router>
          <div className="App">
            <Navigation
              isAuthenticated={isAuthenticated}
              onLogout={handleLogout}
              toggleMobileNav={toggleMobileNav}
              isMobileNavVisible={mobileNavVisible}
            />

            {/* Breadcrumb trail - only show when authenticated */}
            {isAuthenticated && <Breadcrumbs />}

            {/* Balance update alerts - only show when authenticated */}
            {isAuthenticated && <BalanceUpdateAlert />}

            {/* Stop-loss notifications - only show when authenticated */}
            {isAuthenticated && <StopLossNotification />}
            <div className="content-container">
              {isAuthenticated && <NavigationPanel mobileVisible={mobileNavVisible} />}
              <main
                className="flex-grow-1 p-4"
                style={{
                  transition: 'padding-left 0.3s ease',
                  paddingLeft: mainLeftOffset,
                  paddingTop: '0', /* Remove top padding to align borders perfectly */
                  marginLeft: '0',
                  borderLeft: 'none'
                }}
              >
                {showAutoLogoutAlert && (
                  <div className="alert alert-warning alert-dismissible fade show" role="alert">
                    <strong>Session Timeout:</strong> You have been logged out due to inactivity.
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setShowAutoLogoutAlert(false)} 
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                
                {isAuthenticated && passwordExpired && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <strong>Password Expired:</strong> Your password has expired. Please update your password in your profile settings.
                    <a href="/profile" className="btn btn-sm btn-primary ms-3">Update Password</a>
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setPasswordExpired(false)} 
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                
                <Routes>
                  <Route path="/login" element={
                    !isAuthenticated ? 
                      <Login onLogin={handleLogin} /> : 
                      <Navigate to="/dashboard" />
                  } />
                  <Route path="/register" element={
                    !isAuthenticated ? 
                      <Register onLogin={handleLogin} /> : 
                      <Navigate to="/dashboard" />
                  } />
                  <Route path="/dashboard" element={
                    isAuthenticated ? 
                      <Dashboard userId={userId} /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/portfolio/:portfolioId" element={
                    isAuthenticated ? 
                      <PortfolioDetails /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/account/:accountId" element={
                    isAuthenticated ? 
                      <AccountDetails /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/tickers" element={
                    isAuthenticated ? 
                      <AggregatedTickers /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/all-accounts" element={
                    isAuthenticated ? 
                      <AllAccounts /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/search" element={
                    isAuthenticated ? 
                      <TickerSearch /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/transactions" element={
                    isAuthenticated ? 
                      <Transactions /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/trades" element={
                    isAuthenticated ? 
                      <Trades /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/trades/:tradeId" element={
                    isAuthenticated ? 
                      <TradeDetails /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/plan" element={
                    isAuthenticated ? <Plan /> : <Navigate to="/login" />
                  } />
                  <Route path="/notes" element={
                    isAuthenticated ? <Notes /> : <Navigate to="/login" />
                  } />
                  <Route path="/notes/:symbol" element={
                    isAuthenticated ? <NotesDetails /> : <Navigate to="/login" />
                  } />
                  <Route path="/wisdom" element={
                    isAuthenticated ? <Wisdom /> : <Navigate to="/login" />
                  } />
                  <Route path="/activity-log" element={
                    isAuthenticated && isAdmin ? 
                      <ActivityLog /> : 
                      isAuthenticated ?
                        <Navigate to="/dashboard" replace /> :
                        <Navigate to="/login" />
                  } />
                  <Route path="/settings" element={
                    isAuthenticated ? 
                      <Settings /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/admin-settings" element={
                    isAuthenticated && isAdmin ? 
                      <AdminSettings /> : 
                      isAuthenticated ?
                        <Navigate to="/dashboard" replace /> :
                        <Navigate to="/login" />
                  } />
                  <Route path="/profile" element={
                    isAuthenticated ? 
                      <Profile /> : 
                      <Navigate to="/login" />
                  } />
                  <Route path="/watchlist" element={<Watchlist />} />
                  <Route path="/ants" element={<Ants />} />
                  <Route path="/new-ipos" element={<NewIPOs />} />
                  <Route path="/etfs" element={<ETFs />} />
                  <Route path="/stopouts" element={<Stopouts />} />
                  <Route path="/leaders" element={<Leaders />} />
                  <Route path="/vcp" element={<VCP />} />
                  <Route path="/basing" element={<Basing />} />
                  <Route path="/eps" element={<EPS />} />
                  <Route path="/athigh" element={<ATHigh />} />
                  <Route path="/turnarounds" element={<Turnarounds />} />
                  <Route path="/cyclicals" element={<Cyclicals />} />
                  <Route path="/gleaders" element={<GLeaders />} />
                  <Route path="/gleaders/:themeName" element={<GLeaderDetails />} />
                  <Route path="/inverses" element={<Inverses />} />
                  <Route path="/steroids" element={<Steroids />} />
                  <Route path="/hvol" element={<HVol />} />
                  <Route path="/minervini" element={<Minervini />} />
                  <Route path="/turnarounds" element={<Turnarounds />} />
                  <Route path="/strategies" element={<Strategies />} />
                  <Route path="/screen" element={<Screen />} />
                  <Route path="/themes" element={<Themes />} />
                  <Route path="/themes/:themeName" element={<ThemeDetails />} />
                  <Route path="/readylist" element={<Readylist />} />
                  <Route path="/livermore" element={<Livermore />} />
                  <Route path="/play" element={<Play />} />
                  <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} />} />
                </Routes>
              </main>
            </div>

            {/* Global persistent notes modal */}
            <NotesModalManager isAuthenticated={isAuthenticated} />
            {isAuthenticated && <GlobalNotesModal />}
          </div>
        </Router>
        </NotesModalProvider>
      </MarketConditionProvider>
    </ThemeProvider>
  );
}

export default App; 
